import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Video, Image as ImageIcon, Trash2, Eye, ArrowLeft, Upload, Sparkles, X, GripVertical, Mic, QrCode, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getTheme } from '../utils/colors';
import imageCompression from 'browser-image-compression';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Lightbox from '../components/Lightbox';
import LanguageSelector from '../components/LanguageSelector';
import QRCodeGenerator from '../components/QRCodeGenerator';
import { HelpMenu } from '../components/InteractiveGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable media item component
const SortableMediaItem = ({ media, index, onRemove, onImageClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: media.filename || `media-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-3xl border border-border/40 bg-card/50 backdrop-blur-xl shadow-lg overflow-hidden relative group"
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-4 left-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`drag-handle-${index}`}
      >
        <GripVertical className="w-5 h-5" strokeWidth={1.5} />
      </div>

      {media.content_type.startsWith('image/') ? (
        <div 
          className="w-full flex items-center justify-center bg-secondary/20 p-4 cursor-pointer"
          onClick={() => onImageClick(index)}
        >
          <img
            data-testid={`preview-image-${index}`}
            src={`${BACKEND_URL}${media.url}`}
            alt="Preview"
            style={{ 
              maxWidth: '100%',
              maxHeight: '70vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
      ) : media.content_type.startsWith('audio/') ? (
        <div className="w-full p-6 bg-secondary/20 flex flex-col items-center gap-3">
          <Mic className="w-12 h-12 text-muted-foreground" strokeWidth={1.5} />
          <audio
            data-testid={`preview-audio-${index}`}
            src={`${BACKEND_URL}${media.url}`}
            controls
            className="w-full max-w-md"
          />
        </div>
      ) : (
        <video
          data-testid={`preview-video-${index}`}
          src={`${BACKEND_URL}${media.url}`}
          controls
          playsInline
          preload="metadata"
          className="w-full h-auto"
          style={{ maxHeight: '400px', objectFit: 'contain' }}
        />
      )}
      
      <button
        data-testid={`remove-media-${index}`}
        onClick={() => onRemove(index)}
        className="absolute top-4 right-4 bg-destructive text-destructive-foreground rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:scale-110 active:scale-95"
      >
        <Trash2 className="w-5 h-5" strokeWidth={1.5} />
      </button>
    </div>
  );
};

const UploadPage = () => {
  const { nfcCode } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState({ message: '', description: '', media_files: [], theme: 'default' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [aiContext, setAiContext] = useState('');
  const [compressionProgress, setCompressionProgress] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showQRButton, setShowQRButton] = useState(true);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoCameraInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = content.media_files.findIndex(
        (item) => (item.filename || `media-${content.media_files.indexOf(item)}`) === active.id
      );
      const newIndex = content.media_files.findIndex(
        (item) => (item.filename || `media-${content.media_files.indexOf(item)}`) === over.id
      );
      
      setContent(prev => ({
        ...prev,
        media_files: arrayMove(prev.media_files, oldIndex, newIndex)
      }));
      toast.success(t('upload.photosReordered'));
    }
  };

  const [tagNotFound, setTagNotFound] = useState(false);

  useEffect(() => {
    loadContent();
    loadUploadSettings();
  }, [nfcCode]);

  const loadUploadSettings = async () => {
    try {
      // Fetch tag-specific settings (which includes global + tag-level QR settings)
      const response = await axios.get(`${API}/public/tag-settings/${nfcCode}`);
      setShowQRButton(response.data.show_qr_on_upload !== false);
    } catch (error) {
      // Default to showing the button if we can't fetch settings
      setShowQRButton(true);
    }
  };

  const loadContent = async () => {
    try {
      // First check if tag exists
      const existsResponse = await axios.get(`${API}/tags/${nfcCode}/exists`);
      if (!existsResponse.data.exists) {
        setTagNotFound(true);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/tags/${nfcCode}`);
      const tagData = response.data;
      
      // Check if tag is locked (has existing content)
      const hasContent = tagData.message || tagData.description || (tagData.media_files && tagData.media_files.length > 0);
      
      if (hasContent) {
        // Tag is locked, redirect to view page immediately
        navigate(`/${nfcCode}`, { replace: true });
        return;
      }
      
      setContent({
        ...tagData,
        theme: tagData.theme || 'default',
        expires_at: tagData.expires_at || null,
        goes_live_at: tagData.goes_live_at || null,
        notify_email: tagData.notify_email || '',
        notify_enabled: tagData.notify_enabled || false
      });
    } catch (error) {
      console.error('Error loading content:', error);
      if (error.response?.status === 404) {
        setTagNotFound(true);
      } else {
        toast.error('Failed to load content');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    if (file.size > 500 * 1024 * 1024) {
      toast.error('File size exceeds 500MB limit');
      return;
    }

    setUploading(true);
    let fileToUpload = file;
    const originalSize = file.size;

    // Compress images before upload
    if (file.type.startsWith('image/') && file.size > 500 * 1024) {
      try {
        setCompressionProgress({ stage: 'compressing', progress: 0, originalSize });
        
        const options = {
          maxSizeMB: 2,
          maxWidthOrHeight: 2048,
          useWebWorker: true,
          onProgress: (progress) => {
            setCompressionProgress({ stage: 'compressing', progress, originalSize });
          }
        };
        
        fileToUpload = await imageCompression(file, options);
        const compressedSize = fileToUpload.size;
        const savedPercent = Math.round((1 - compressedSize / originalSize) * 100);
        
        if (savedPercent > 5) {
          toast.success(`${t('upload.compressed')} ${savedPercent}% (${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)})`);
        }
        
        setCompressionProgress({ stage: 'uploading', progress: 0, originalSize, compressedSize });
      } catch (error) {
        console.error('Compression error:', error);
        toast.warning(t('upload.compressionFailed'));
        fileToUpload = file;
      }
    } else if (file.type.startsWith('video/')) {
      setCompressionProgress({ stage: 'uploading', progress: 0, originalSize, isVideo: true });
      toast.info(t('upload.videoProcessing'));
    } else {
      setCompressionProgress({ stage: 'uploading', progress: 0, originalSize });
    }

    const CHUNK_SIZE = 1.5 * 1024 * 1024; // 1.5MB chunks (under proxy limit)
    const useChunked = fileToUpload.size > 1 * 1024 * 1024; // Always chunk files over 1MB

    try {
      let responseData;

      const doChunkedUpload = async () => {
        const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);
        setCompressionProgress(prev => ({ ...prev, stage: 'uploading', progress: 0, totalChunks }));

        // 1. Init
        const initForm = new FormData();
        initForm.append('filename', fileToUpload.name || file.name);
        initForm.append('content_type', fileToUpload.type || file.type);
        initForm.append('total_size', fileToUpload.size);
        initForm.append('total_chunks', totalChunks);

        const initRes = await axios.post(`${API}/upload/init`, initForm, { timeout: 30000 });
        const { upload_id } = initRes.data;

        // 2. Upload chunks with retry per chunk
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, fileToUpload.size);
          const blob = fileToUpload.slice(start, end);

          const chunkForm = new FormData();
          chunkForm.append('upload_id', upload_id);
          chunkForm.append('chunk_index', i);
          chunkForm.append('chunk', blob, `chunk_${i}`);

          let retries = 3;
          while (retries > 0) {
            try {
              await axios.post(`${API}/upload/chunk`, chunkForm, {
                timeout: 60000,
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              break;
            } catch (chunkErr) {
              retries--;
              if (retries === 0) throw chunkErr;
              await new Promise(r => setTimeout(r, 1000));
            }
          }

          const progress = Math.round(((i + 1) / totalChunks) * 100);
          setCompressionProgress(prev => ({ ...prev, stage: 'uploading', progress }));
        }

        // 3. Complete — server merges and processes
        setCompressionProgress(prev => ({ ...prev, stage: 'processing', progress: 100 }));
        const completeForm = new FormData();
        completeForm.append('upload_id', upload_id);
        const completeRes = await axios.post(`${API}/upload/complete`, completeForm, { timeout: 600000 });
        return completeRes.data;
      };

      const doRegularUpload = async () => {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setCompressionProgress(prev => ({ ...prev, stage: 'uploading', progress }));
          }
        });
        return response.data;
      };

      if (useChunked) {
        try {
          responseData = await doChunkedUpload();
        } catch (chunkedErr) {
          // Fallback to regular upload if chunked endpoints not available
          console.warn('Chunked upload failed, trying regular upload:', chunkedErr.message);
          responseData = await doRegularUpload();
        }
      } else {
        try {
          responseData = await doRegularUpload();
        } catch (regularErr) {
          // Fallback to chunked if regular fails (proxy limit)
          if (fileToUpload.size > 512 * 1024) {
            console.warn('Regular upload failed, trying chunked:', regularErr.message);
            toast.info('Retrying with chunked upload...');
            responseData = await doChunkedUpload();
          } else {
            throw regularErr;
          }
        }
      }
      
      // Show compression result for videos
      if (responseData.compressed) {
        const savedPercent = Math.round((1 - responseData.size / responseData.original_size) * 100);
        toast.success(`${t('upload.compressed')} ${savedPercent}% (${formatFileSize(responseData.original_size)} → ${formatFileSize(responseData.size)})`);
      }
      
      setContent(prev => ({
        ...prev,
        media_files: [...prev.media_files, responseData]
      }));
      toast.success(t('upload.uploadSuccess'));
    } catch (error) {
      console.error('Upload error:', error);
      const msg = error.response?.data?.detail || t('upload.uploadFailed');
      toast.error(msg);
    } finally {
      setUploading(false);
      setCompressionProgress(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append('message', content.message);
    formData.append('description', content.description);
    formData.append('theme', content.theme);
    formData.append('media_files', JSON.stringify(content.media_files));
    
    // Add expiration date if set
    if (content.expires_at) {
      formData.append('expires_at', content.expires_at);
    }
    
    // Add goes live date if set (scheduled content)
    if (content.goes_live_at) {
      formData.append('goes_live_at', content.goes_live_at);
    }
    
    // Add notification settings
    if (content.notify_email) {
      formData.append('notify_email', content.notify_email);
    }
    formData.append('notify_enabled', content.notify_enabled);

    try {
      await axios.post(`${API}/tags/${nfcCode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('upload.contentSaved'));
      navigate(`/${nfcCode}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('upload.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const removeMedia = (index) => {
    setContent(prev => ({
      ...prev,
      media_files: prev.media_files.filter((_, i) => i !== index)
    }));
    toast.success(t('upload.mediaRemoved'));
  };

  const generateAIMessage = async () => {
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/ai/generate-message`, {
        theme: content.theme || 'default',
        title: content.description,
        context: aiContext
      });
      
      setContent(prev => ({ ...prev, message: response.data.message }));
      setShowAIModal(false);
      setAiContext('');
      toast.success(t('upload.aiGenerated'));
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(t('upload.aiFailed'));
    } finally {
      setGeneratingAI(false);
    }
  };

  // Download QR code with styling options
  const downloadQRCode = async (nfcCodeToDownload, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.fgColor) params.append('fg_color', options.fgColor.replace('#', ''));
      if (options.bgColor) params.append('bg_color', options.bgColor.replace('#', ''));
      if (options.size) params.append('size', options.size);
      if (options.dotStyle) params.append('dot_style', options.dotStyle);
      
      const queryString = params.toString();
      const url = `${API}/admin/qr-code/${nfcCodeToDownload}${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${nfcCodeToDownload}-qr.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('upload.qrDownloaded'));
    } catch (error) {
      console.error('QR download error:', error);
      toast.error(t('upload.qrFailed'));
    }
  };

  const selectedTheme = getTheme(content.theme);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-pulse text-muted-foreground">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (tagNotFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-6">
            <X className="w-10 h-10 text-destructive" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight mb-4">
            {t('upload.invalidTag')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('upload.invalidTagDesc')}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <button
                data-testid="back-button"
                onClick={() => navigate(`/${nfcCode}`)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                <span>{t('back')}</span>
              </button>
              <LanguageSelector onChange={(lang) => i18n.changeLanguage(lang)} />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold font-heading tracking-tight mb-4">
                {t('upload.title')}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('upload.subtitle')}
              </p>
            </div>
          </div>

          {/* Color Selector */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-2">{t('upload.backgroundColor')}</label>
            <p className="text-xs text-muted-foreground mb-4">{t('upload.backgroundColorDesc')}</p>
            <div className="flex flex-wrap gap-3">
              {[
                { id: 'default', color: '#FDFCF8', border: '#E4E4E7' },
                { id: 'pink', color: '#FDF2F8', border: '#F9A8D4' },
                { id: 'blue', color: '#EFF6FF', border: '#93C5FD' },
                { id: 'green', color: '#F0FDF4', border: '#86EFAC' },
                { id: 'purple', color: '#FAF5FF', border: '#C4B5FD' },
                { id: 'yellow', color: '#FEFCE8', border: '#FDE047' },
                { id: 'orange', color: '#FFF7ED', border: '#FDBA74' },
                { id: 'red', color: '#FEF2F2', border: '#FCA5A5' },
                { id: 'teal', color: '#F0FDFA', border: '#5EEAD4' },
                { id: 'slate', color: '#F8FAFC', border: '#CBD5E1' },
              ].map((colorOption) => (
                <button
                  key={colorOption.id}
                  data-testid={`color-${colorOption.id}`}
                  onClick={() => setContent(prev => ({ ...prev, theme: colorOption.id }))}
                  className={`w-10 h-10 rounded-full transition-all ${
                    content.theme === colorOption.id
                      ? 'ring-2 ring-offset-2 ring-primary scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{
                    background: colorOption.color,
                    border: `2px solid ${colorOption.border}`
                  }}
                  title={colorOption.id === 'default' ? 'Default (cream)' : colorOption.id}
                />
              ))}
            </div>
          </div>

          {/* Description Input */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-3">{t('upload.titleLabel')}</label>
            <input
              data-testid="description-input"
              type="text"
              value={content.description}
              onChange={(e) => setContent(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('upload.titlePlaceholder')}
              className="w-full border-0 border-b-2 border-input bg-transparent px-0 py-4 text-lg focus:outline-none focus:border-primary placeholder:text-muted-foreground/50 transition-colors"
            />
          </div>

          {/* Message Input */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium">{t('upload.messageLabel')}</label>
              <button
                data-testid="ai-generate-button"
                onClick={() => setShowAIModal(true)}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                {t('upload.generateWithAI')}
              </button>
            </div>
            <textarea
              data-testid="message-input"
              value={content.message}
              onChange={(e) => setContent(prev => ({ ...prev, message: e.target.value }))}
              placeholder={t('upload.messagePlaceholder')}
              rows={6}
              className="w-full border-0 border-b-2 border-input bg-transparent px-0 py-4 text-lg focus:outline-none focus:border-primary placeholder:text-muted-foreground/50 transition-colors resize-none"
            />
          </div>

          {/* AI Modal */}
          {showAIModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background rounded-3xl border border-border shadow-2xl p-8 max-w-md w-full"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-2xl font-bold font-heading">{t('upload.aiWriter')}</h2>
                </div>
                
                <p className="text-muted-foreground mb-6">
                  {t('upload.aiWriterDesc')} <span className="font-medium text-foreground">{content.description || t('upload.specialOccasion')}</span>.
                </p>

                <textarea
                  data-testid="ai-context-input"
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  placeholder={t('upload.aiContextPlaceholder')}
                  rows={4}
                  className="w-full border-2 border-border rounded-2xl bg-transparent px-4 py-3 text-base focus:outline-none focus:border-primary placeholder:text-muted-foreground/50 transition-colors resize-none mb-6"
                />

                <div className="flex gap-3">
                  <button
                    data-testid="ai-modal-cancel"
                    onClick={() => {
                      setShowAIModal(false);
                      setAiContext('');
                    }}
                    className="flex-1 bg-secondary text-secondary-foreground rounded-full px-6 py-4 font-medium transition-all hover:scale-105 active:scale-95"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    data-testid="ai-modal-generate"
                    onClick={generateAIMessage}
                    disabled={generatingAI}
                    className="flex-1 bg-primary text-primary-foreground rounded-full px-6 py-4 font-medium transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {generatingAI ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        {t('upload.generating')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                        {t('upload.generate')}
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Preview Modal */}
          {showPreviewModal && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl"
                style={{ background: selectedTheme.colors.background }}
              >
                {/* Close button */}
                <div className="sticky top-0 z-10 flex justify-end p-4">
                  <button
                    data-testid="close-preview-modal"
                    onClick={() => setShowPreviewModal(false)}
                    className="bg-black/20 hover:bg-black/30 text-white rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" strokeWidth={1.5} />
                  </button>
                </div>

                <div className="px-6 pb-8 -mt-8">
                  {/* Title */}
                  <h1 
                    className="text-3xl md:text-4xl font-bold text-center mb-6"
                    style={{ color: selectedTheme.colors.foreground }}
                  >
                    {content.description || 'Your Title Here'}
                  </h1>

                  {/* Message */}
                  {content.message && (
                    <div 
                      className="rounded-2xl border p-6 mb-6"
                      style={{
                        background: selectedTheme.colors.cardBg || '#FFFFFF',
                        borderColor: selectedTheme.colors.secondary,
                        color: selectedTheme.colors.foreground
                      }}
                    >
                      <p className="text-base leading-relaxed whitespace-pre-wrap">{content.message}</p>
                    </div>
                  )}

                  {/* Media */}
                  {content.media_files && content.media_files.length > 0 && (
                    <div className="space-y-4">
                      {content.media_files.map((media, index) => (
                        <div
                          key={index}
                          className="rounded-2xl border overflow-hidden"
                          style={{
                            borderColor: selectedTheme.colors.primary,
                            background: selectedTheme.colors.cardBg || '#FFFFFF'
                          }}
                        >
                          {media.content_type.startsWith('image/') ? (
                            <div 
                              className="w-full flex items-center justify-center p-2"
                              style={{ background: selectedTheme.colors.secondary }}
                            >
                              <img
                                src={`${BACKEND_URL}${media.url}`}
                                alt="Preview"
                                style={{ 
                                  maxWidth: '100%',
                                  maxHeight: '50vh',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                          ) : (
                            <video
                              src={`${BACKEND_URL}${media.url}`}
                              controls
                              playsInline
                              preload="metadata"
                              className="w-full h-auto"
                              style={{ maxHeight: '50vh', objectFit: 'contain' }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty state */}
                  {!content.message && !content.description && content.media_files.length === 0 && (
                    <div 
                      className="text-center py-12 rounded-2xl border"
                      style={{
                        background: selectedTheme.colors.cardBg || '#FFFFFF',
                        borderColor: selectedTheme.colors.primary,
                        color: selectedTheme.colors.foreground
                      }}
                    >
                      <p className="text-muted-foreground">Add a title, message, or photos to see preview</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}

          {/* Upload Actions */}
          <div className="mb-12">
            <label className="block text-sm font-medium mb-2">{t('upload.media')}</label>
            <p className="text-xs text-muted-foreground mb-4">{t('upload.uploadContent')}</p>

            <div className="grid grid-cols-2 gap-4">
              {/* Photo Gallery */}
              <button
                data-testid="photo-gallery-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-2 border-dashed border-border rounded-3xl p-8 flex flex-col items-center justify-center gap-3 bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer group"
              >
                <ImageIcon className="w-8 h-8 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                <span className="text-sm font-medium">{t('upload.photoGallery')}</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.bmp,.tiff"
                multiple
                onChange={(e) => {
                  Array.from(e.target.files).forEach(file => handleFileUpload(file));
                  e.target.value = '';
                }}
                className="hidden"
              />

              {/* Video Gallery */}
              <button
                data-testid="video-gallery-button"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploading}
                className="border-2 border-dashed border-border rounded-3xl p-8 flex flex-col items-center justify-center gap-3 bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer group"
              >
                <Video className="w-8 h-8 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                <span className="text-sm font-medium">{t('upload.videoGallery')}</span>
              </button>
              <input
                ref={videoInputRef}
                type="file"
                accept=".mp4,.mov,.avi,.mkv,.webm,.m4v,.3gp,.wmv,.flv,.mpeg,.mpg"
                multiple
                onChange={(e) => {
                  Array.from(e.target.files).forEach(file => handleFileUpload(file));
                  e.target.value = '';
                }}
                className="hidden"
              />

              {/* Camera */}
              <button
                data-testid="camera-button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="border-2 border-dashed border-border rounded-3xl p-8 flex flex-col items-center justify-center gap-3 bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer group"
              >
                <Camera className="w-8 h-8 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                <span className="text-sm font-medium">{t('upload.takePhoto')}</span>
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  if (e.target.files[0]) handleFileUpload(e.target.files[0]);
                  e.target.value = '';
                }}
                className="hidden"
              />

              {/* Video Camera */}
              <button
                data-testid="video-camera-button"
                onClick={() => videoCameraInputRef.current?.click()}
                disabled={uploading}
                className="border-2 border-dashed border-border rounded-3xl p-8 flex flex-col items-center justify-center gap-3 bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer group"
              >
                <Video className="w-8 h-8 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                <span className="text-sm font-medium">{t('upload.recordVideo')}</span>
              </button>
              <input
                ref={videoCameraInputRef}
                type="file"
                accept="video/*"
                capture="environment"
                onChange={(e) => {
                  if (e.target.files[0]) handleFileUpload(e.target.files[0]);
                  e.target.value = '';
                }}
                className="hidden"
              />

              {/* Audio */}
              <button
                data-testid="audio-button"
                onClick={() => audioInputRef.current?.click()}
                disabled={uploading}
                className="border-2 border-dashed border-border rounded-3xl p-8 flex flex-col items-center justify-center gap-3 bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer group"
              >
                <Mic className="w-8 h-8 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                <span className="text-sm font-medium">{t('upload.audioFile')}</span>
              </button>
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  if (e.target.files[0]) handleFileUpload(e.target.files[0]);
                  e.target.value = '';
                }}
                className="hidden"
              />

              {/* Get QR Code */}
              {showQRButton && (
                <button
                  data-testid="qr-code-grid-button"
                  onClick={() => setShowQRModal(true)}
                  className="border-2 border-dashed border-border rounded-3xl p-8 flex flex-col items-center justify-center gap-3 bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer group"
                >
                  <QrCode className="w-8 h-8 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{t('upload.getQRCode')}</span>
                </button>
              )}
            </div>
            {uploading && compressionProgress && (
              <div className="mt-4 p-4 bg-secondary/30 rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {compressionProgress.stage === 'compressing' ? (
                      <>{t('upload.compressing')} ({formatFileSize(compressionProgress.originalSize)})</>
                    ) : compressionProgress.stage === 'processing' ? (
                      <>Processing file on server...</>
                    ) : compressionProgress.isVideo ? (
                      <>{t('upload.uploadingVideo')} ({formatFileSize(compressionProgress.originalSize)})</>
                    ) : compressionProgress.totalChunks ? (
                      <>Uploading ({formatFileSize(compressionProgress.originalSize)})...</>
                    ) : (
                      <>{t('upload.uploading')}{compressionProgress.compressedSize ? ` (${formatFileSize(compressionProgress.compressedSize)})` : ''}...</>
                    )}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {compressionProgress.progress}%
                  </span>
                </div>
                <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all duration-300 ease-out bg-primary"
                    style={{ width: `${compressionProgress.progress}%` }}
                  />
                </div>
                {compressionProgress.stage === 'compressing' && compressionProgress.compressedSize && (
                  <p className="text-xs text-green-600 mt-2">
                    {t('upload.compressed')} {Math.round((1 - compressionProgress.compressedSize / compressionProgress.originalSize) * 100)}% 
                    ({formatFileSize(compressionProgress.originalSize)} → {formatFileSize(compressionProgress.compressedSize)})
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Media Preview */}
          {content.media_files && content.media_files.length > 0 && (
            <div className="mb-12">
              <label className="block text-sm font-medium mb-2">{t('upload.previewCount')} ({content.media_files.length})</label>
              <p className="text-xs text-muted-foreground mb-4">{t('upload.dragToReorder')}</p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={content.media_files.map((m, i) => m.filename || `media-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {content.media_files.map((media, index) => (
                      <SortableMediaItem
                        key={media.filename || `media-${index}`}
                        media={media}
                        index={index}
                        onRemove={removeMedia}
                        onImageClick={setLightboxIndex}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {/* Lightbox for upload page */}
          {lightboxIndex !== null && content.media_files && (
            <Lightbox
              media={content.media_files}
              currentIndex={lightboxIndex}
              onClose={() => setLightboxIndex(null)}
              onNavigate={setLightboxIndex}
            />
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 flex-wrap">
            <button
              data-testid="preview-button"
              onClick={() => setShowPreviewModal(true)}
              className="flex-1 min-w-[120px] bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full px-6 py-6 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
            >
              <Eye className="w-5 h-5" strokeWidth={1.5} />
              {t('upload.preview')}
            </button>
            {showQRButton && (
              <button
                data-testid="qr-code-button-main"
                onClick={() => setShowQRModal(true)}
                className="flex-1 min-w-[120px] bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full px-6 py-6 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
              >
                <QrCode className="w-5 h-5" strokeWidth={1.5} />
                {t('upload.getQR')}
              </button>
            )}
            <button
              data-testid="save-button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 min-w-[120px] bg-primary text-primary-foreground rounded-full px-6 py-6 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? t('upload.saving') : t('save')}
            </button>
          </div>

          {/* QR Code Generator Modal */}
          {showQRModal && (
            <QRCodeGenerator
              tagId={nfcCode}
              onClose={() => setShowQRModal(false)}
            />
          )}
        </motion.div>
      </div>

      {/* Interactive Help Menu */}
      <HelpMenu availableTours={['beginner']} />
    </div>
  );
};

export default UploadPage;